// Prompt 19 PART C — public /help page content (Markdown).
import { PRICE_LABEL } from "./pricing";
import { livePlatforms, comingSoon, joinList, label } from "../lib/platforms";
export const HELP_HEADING_IDS = {
  "What Lingua Franca is": "what-is-lingua-franca",
  "Quick start": "quick-start",
  "Connect your accounting system": "connect-your-accounting-system",
  "QuickBooks Online": "connect-quickbooks",
  "Xero": "connect-xero",
  "Zoho Books": "connect-zoho-books",
  "Microsoft Dynamics 365 Business Central": "connect-business-central",
  "FreshBooks": "connect-freshbooks",
  "NetSuite (coming soon)": "netsuite",
  "Receiving GL": "receiving-gl",
  "Billing email": "billing-email",
  "Approving vendors": "approving-vendors",
  "Mapping items": "mapping-items",
  "Invoice statuses": "invoice-statuses",
  "Fixing Held invoices": "fixing-held-invoices",
  "Export and print": "export-and-print",
  "Disconnecting": "disconnecting",
  "Pricing": "pricing",
  "Security": "security",
  "FAQ": "faq",
  "Contact support": "contact-support",
};

export const HELP = `# Lingua Franca Help

Everything you need to set up Lingua Franca and keep invoices flowing. If you get stuck, email support@linguafrancainv.com.

**On this page:** [What Lingua Franca is](#what-is-lingua-franca) · [Quick start](#quick-start) · [Connect your accounting system](#connect-your-accounting-system) · [Receiving GL](#receiving-gl) · [Billing email](#billing-email) · [Approving vendors](#approving-vendors) · [Mapping items](#mapping-items) · [Invoice statuses](#invoice-statuses) · [Fixing Held invoices](#fixing-held-invoices) · [Export and print](#export-and-print) · [Disconnecting](#disconnecting) · [Pricing](#pricing) · [Security](#security) · [FAQ](#faq) · [Contact support](#contact-support)

---

## What Lingua Franca is

Lingua Franca (LF) is an invoice clearinghouse. It takes an invoice that a vendor has already created in their accounting system and delivers it as a bill into their customer's accounting system, with the lines, amounts and tax intact. Nobody has to re-type it.

A few things LF does **not** do:

- **LF doesn't create or edit invoices.** Invoices are created in the sender's own accounting system. To change an invoice, change it there.
- **LF doesn't move money.** It never takes, sends or holds payments. The Payment column in LF is read-only and comes from the receiver's accounting system.
- **LF doesn't give accounting or tax advice.** You decide how bills are coded, and you should review them before you approve or pay them.

LF works with **${joinList(livePlatforms().map(label))}**.${comingSoon().map((p) => ` ${p.name} is coming soon.`).join("")}

---

## Quick start

Your dashboard has a **Getting started** checklist that ticks itself off as you go. The steps are:

1. **Connect your accounting system** on the Connections page.
2. **Check your Receiving GL.** This is the accounting system your incoming bills go into. Your first connection is chosen automatically.
3. **Confirm your billing email.** Vendors' invoices find you by the email address they bill.
4. **Approve your first vendor.** The first time a new vendor sends you an invoice, you approve them once.
5. **Map items to GL accounts.** Tell LF which expense account each item belongs in.
6. **Get your first invoice synced.** Once the vendor is approved and the lines are mapped, the bill lands in your accounting system.

**Sending invoices?** Connect your accounting system and keep invoicing the way you do today. Make sure the customer's email on each invoice is the billing email they use in LF. That's how LF knows where to deliver it.

---

## Connect your accounting system

Go to the **Connections** page and click **Connect** on the card for your accounting system. You'll be sent to that provider's own sign-in page. Sign in there and approve access. LF never sees your password. When you come back, the card shows **LIVE**.

The provider's screens change from time to time, so the steps below are a general guide.

### QuickBooks Online

1. Click **Connect to QuickBooks** on the QuickBooks card.
2. Sign in to your Intuit account.
3. If you have more than one company, choose the one you want to connect, then approve access.

To receive bills in QuickBooks, your company needs **QuickBooks Online Essentials, Plus or Advanced**. Simple Start doesn't support bills, so QuickBooks rejects the push and those invoices end up **Failed**, with the reason shown on the invoice. Sending invoices from Simple Start is fine.

### Xero

1. Click **Connect to Xero** on the Xero card.
2. Sign in to Xero.
3. Choose the organisation you want to connect, then allow access.

Depending on your Xero settings, bills from LF may arrive as drafts for you to review and approve in Xero.

### Zoho Books

1. Click **Connect to Zoho Books** on the Zoho Books card.
2. Sign in to your Zoho account.
3. If Zoho asks, choose your organization, then accept the requested access.

### Microsoft Dynamics 365 Business Central

1. Click **Connect to Business Central** on the Business Central card.
2. Sign in with your Microsoft work account and accept the permissions. Some organizations require a Microsoft administrator to approve new apps first. If you see a message about admin approval, ask your IT administrator.
3. Back in LF, check the **Company** on the Business Central card. If your account has only one company, LF selects it for you. The **pick a bc company** selector only appears when your account has more than one company; use it to choose the right one.

Bills arrive in Business Central as draft (unposted) purchase invoices, so you can review and post them as usual.

### FreshBooks

1. Click **Connect to FreshBooks** on the FreshBooks card.
2. Sign in to FreshBooks and allow access.
3. If your login has more than one business, choose the right one from the **pick a freshbooks business** selector on the card.

Bills from LF arrive in FreshBooks under Bills, with the sending business as the vendor. Line items are coded to the expense categories you choose on the Mappings page.

### NetSuite (coming soon)

NetSuite isn't available yet. Click **Notify me when live** on the NetSuite card and we'll let you know when it's ready.

---

## Receiving GL

Your **Receiving GL** is the one accounting system your incoming bills are delivered into. Each company has exactly one.

- When you connect your first accounting system, it becomes your Receiving GL automatically.
- If you've connected more than one, choose which one receives bills on the **Connections** page.
- Changing your Receiving GL only affects invoices that haven't synced yet. Bills already delivered stay where they are.
- Mappings are kept separately for each accounting system. If you switch, you may need to map items for the new one. Your old mappings are kept in case you switch back.
- If your Receiving GL is disconnected, new invoices are held with the reason "Receiving GL not connected" until you reconnect it or choose another.

---

## Billing email

Vendors send invoices to an email address, and LF uses that address to match each invoice to your company.

- The email you signed up with is your first billing email, and it's already confirmed.
- To add another address (for example, ap@yourcompany.com), add it in your company settings. We send a confirmation link to that address, and it only works once the link is clicked.
- Each email address can belong to only one company in LF.
- When you confirm a new address, invoices that were waiting because nobody had claimed that address are matched to you. You'll still need to approve the vendor.

All of your billing emails deliver into your one Receiving GL.

---

## Approving vendors

The first time a new vendor sends you an invoice through LF, they appear on your **Vendors** list as **Pending**. Their invoice shows up in LF, but nothing goes into your accounting system until you decide.

- **Approve** to accept bills from this vendor. Their waiting invoices continue on to your Receiving GL, as long as the items are mapped.
- **Block** if you don't recognize them or don't want their invoices delivered. Blocked vendors' invoices stay in LF but are never pushed to your accounting system.

You only approve a vendor once. Approval is about trusting where the data comes from. It has nothing to do with approving payment.

When a bill is delivered, LF uses the matching vendor in your accounting system, or adds that vendor if it isn't there yet.

---

## Mapping items

Every line on an invoice needs to land in one of your GL accounts, such as Office Supplies or Repairs & Maintenance. A **mapping** tells LF which account to use for each item.

- Open the **Mappings** page to see every item your vendors have billed you for, and choose an account for each one.
- You can also map a line directly from an invoice by clicking **Map** next to it.
- Mappings belong to your current Receiving GL, and the account list comes from that system's chart of accounts. Header or total accounts that can't be posted to aren't shown.
- After an item is mapped, future invoices with that item use the same account automatically.
- If you change your chart of accounts, check your mappings again.

An invoice with any unmapped line waits as **Held** until every line has an account.

---

## Invoice statuses

Every invoice in LF has a status that tells you where it is and what, if anything, you need to do.

| Status | What it means | What to do |
|---|---|---|
| **Held** | The invoice is in LF but can't be delivered yet. The reason is shown on the invoice. | Fix the reason (see below). Most fix themselves once you act. |
| **Pending / Ready** | Nothing is blocking it, and it's waiting for the next sync. | Nothing. Or click **Sync Now** on the invoice to send it right away. |
| **Synced** | The bill has been delivered into the receiver's accounting system. | Review and approve it in your accounting system as usual. |
| **Failed** | Delivery was attempted and the accounting system rejected it. The error is shown on the invoice. | Read the error, fix it (often a mapping or connection issue), then click **Sync Now**. |

**held (all)** isn't a separate status. It's an option in the sync filter on the Invoices page that shows invoices that are held and waiting on you. It also includes **Failed** invoices. Use it to find everything that needs attention in one place. The dashboard's **Needs attention** count links there too.

The **Payment** column is read from the receiver's accounting system: **Not in GL yet** (not delivered), **Unpaid**, **Partial** or **Paid**. LF can't change it.

Once an invoice has synced, LF doesn't update it again. If the vendor changes it after that, they should send a new invoice, or the receiver can adjust the bill in their own accounting system. Before it syncs, changes made in the vendor's system are picked up on the next sync.

---

## Fixing Held invoices

Open the invoice to see why it's held. The usual reasons are:

- **item mapping needed:** one or more lines don't have a GL account. Click **Map** on each line, or use the Mappings page.
- **awaiting vendor approval:** you haven't approved this vendor yet. Approve them on the Vendors page.
- **Vendor blocked:** you blocked this vendor. Unblock them only if that was a mistake.
- **No matching company:** the invoice was sent to an email address that isn't a confirmed billing email in LF. If it's yours, add and confirm that address. If you're the sender, check the customer's email in your accounting system.
- **Receiving GL not connected:** reconnect your accounting system on the Connections page, or choose another Receiving GL.
- **Sender and receiver are the same GL org:** the invoice would be delivered back into the accounting system it came from, so LF won't send it.

**QuickBooks Simple Start** isn't a Held reason: QuickBooks rejects the push, so the invoice shows as **Failed**. Upgrade the QuickBooks company to Essentials, Plus or Advanced, then click **Sync Now**.

After you fix the reason, the invoice moves on at the next sync. You can also click **Sync Now** to try right away. Clicking Sync Now more than once is safe. LF checks first and never creates a duplicate bill.

If a connection expires or is disconnected, LF shows a red banner at the top of the page and emails your confirmed billing addresses. Click **Open connections** to fix it.

---

## Export and print

- **Print or save as PDF:** open an invoice and click **Print / PDF**. Use your browser's "Save as PDF" option to keep a copy.
- **CSV export:** on the Invoices list, apply any filters you want and click **Export CSV**. You get one row per invoice line, ready for Excel. You can also download a CSV for a single invoice from the invoice page.

Exports only ever include your own company's invoices.

---

## Disconnecting

To disconnect an accounting system, open the **Connections** page and click **Disconnect** on its card.

- LF stops syncing with that system right away and deletes its access.
- Bills LF already delivered stay in your accounting system.
- Your invoice history in LF isn't deleted when you disconnect. It's kept as described in our [Privacy Policy](/privacy), and you can ask us to delete it.
- If you disconnect your Receiving GL, new invoices are held until you reconnect or choose another.

You can reconnect at any time. Instructions for removing access from inside each accounting system are on the [Contact & Support](/contact) page.

---

## Pricing

- **Sending invoices is free.** Only the receiving business pays.
- **Free to start.** Free for 6 months or 100 received invoices from signup, whichever comes first. Then $0.25 per received invoice, charged once your balance reaches $5. No card needed to start.
- **After that, ${PRICE_LABEL} per invoice** that syncs into your Receiving GL for the first time. No monthly fee and no minimum.
- Retries, re-syncs and payment-status updates on an invoice that has already synced are never charged again. Invoices that are held, blocked or failed aren't charged.

Full details are in our [Terms of Service](/terms). Questions about billing? Email billing@linguafrancainv.com.

---

## Security

- **We never see your passwords.** You sign in directly with your accounting provider, on their own page (OAuth). LF never sees or stores your accounting system password.
- **We never store card details.** Payments are handled by Stripe, so your card number never touches LF.
- **Only the access we need.** LF asks for the permissions it needs to read invoices and deliver bills.
- **Encrypted.** Data is encrypted in transit (HTTPS), and access tokens are encrypted at rest.
- **Your data isn't for sale.** LF doesn't sell your data or use it for advertising or AI training.
- **You're in control.** Disconnect any time, or ask us to delete your data.

See our [Privacy Policy](/privacy) for details. To report a security concern, email security@linguafrancainv.com with "SECURITY" in the subject line.

---

## FAQ

**Can I edit an invoice in Lingua Franca?**

No. LF delivers invoices exactly as they were created. Make changes in the sender's accounting system before the invoice syncs. After it syncs, send a new invoice or adjust the bill in the receiver's system.

**Does Lingua Franca pay my bills or collect payments?**

No. LF never handles money. You pay bills the way you always do. The Payment column just shows what your accounting system reports.

**Why is my invoice Held?**

Open it. The reason is shown on the invoice. The most common reasons are an unmapped line or a vendor you haven't approved yet. See [Fixing Held invoices](#fixing-held-invoices).

**Will clicking Sync Now twice create a duplicate bill?**

No. LF checks whether the bill already exists before creating it.

**Can I receive bills into more than one accounting system?**

Each company has one Receiving GL at a time. You can connect several systems and switch which one receives bills. Bills already delivered stay where they landed.

**My vendor says they sent an invoice, but I don't see it.**

Check that the customer email on their invoice is one of your confirmed billing emails, and that their accounting system is connected to LF. New invoices are picked up on a regular schedule. If it still doesn't show up, contact support with the invoice number.

**Does my vendor need a Lingua Franca account?**

Yes. Invoices come from the vendor's connected accounting system, so they need to sign up and connect it. Sending is free.

**When will NetSuite be available?**

It's in progress. Click **Notify me when live** on the NetSuite card on the Connections page and we'll let you know.

---

## Contact support

Email **support@linguafrancainv.com**, Monday–Friday, 9 a.m.–5 p.m. US Central (excluding US holidays).

To help us find the problem quickly, include:

- The email address on your LF account
- Which accounting system(s) are involved
- The invoice number(s) and roughly when the sync happened
- Any error message you saw (a screenshot helps)

Please don't email passwords or access tokens. We'll never ask for your accounting system password. More ways to reach us are on the [Contact & Support](/contact) page.
`;
